import http from 'common/http'
import update from 'immutability-helper'
import {
  shouldcreateUserAccount,
  shouldEditUserAccount,
  shouldShowFarmSetupWizard,
} from 'actions/app'
import {
  userFetched,
  updateClient,
  contactInfoFetched,
  organizationsFetched,
  userListFetched,
  userPreferenceFetched,
} from 'actions/user'
import { getPartner } from 'common/misc'

export const fetchUserAccount = () => {
  return (dispatch) => {
    return http
      .get('/user')
      .then((res) => {
        dispatch(userFetched(res.data))
        if (res.data?.eusaDate == null) {
          dispatch(shouldShowFarmSetupWizard())
        }
        return 'UserFetched'
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          dispatch(shouldcreateUserAccount())
          return 'UserEmpty'
        }
        throw err
      })
  }
}

export const createUserAccount = (data) => {
  return (dispatch) => {
    return http
      .post('/user', data)
      .then(() => {})
      .catch((err) => {
        if (err.response && err.response.status) {
          dispatch(shouldcreateUserAccount())
        }
      })
  }
}

export const fetchOrganizations = () => {
  return (dispatch) => {
    return http.get('/partner').then((res) => {
      dispatch(organizationsFetched(res.data))
    })
  }
}

const getAuth0TokenCacheKey = () =>
  `@@auth0spajs@@::${AUTH_CID}::https://api.farmtrx.com::openid profile email offline_access`

export const switchClient = (
  tenantId,
  options: { wizard?: boolean; redirectTo?: string; accessToken?: string } = {},
) => {
  return () => {
    return http
      .get(
        '/tenant/switch/' + tenantId,
        options.accessToken
          ? { headers: { Authorization: `Bearer ${options.accessToken}` } }
          : undefined,
      )
      .then((res) => {
        const data = JSON.parse(localStorage.getItem(getAuth0TokenCacheKey())!)
        if (localStorage.getItem('impersonate') !== 'true') {
          localStorage.setItem('otok', data.body.access_token)
        }
        localStorage.setItem('impersonate', 'true')
        const updateData = update(data, {
          body: { access_token: { $set: res.data } },
        })
        localStorage.setItem(
          getAuth0TokenCacheKey(),
          JSON.stringify(updateData),
        )
        if (options.wizard) {
          localStorage.setItem('wizardActiveStep', '0')
        } else {
          localStorage.removeItem('wizardActiveStep')
        }
        window.location.href = options.redirectTo ?? '/'
      })
  }
}

// An impersonated token carries the visited tenant's partner id, so switching
// again must go through the admin's own (refreshed) token instead.
export const impersonateTenant = async (
  tenantId: string,
  redirectTo: string,
  getAdminAccessToken: () => Promise<string | undefined>,
) => {
  const restoredOriginalToken = localStorage.getItem('impersonate') === 'true'
  if (restoredOriginalToken) {
    const data = JSON.parse(localStorage.getItem(getAuth0TokenCacheKey())!)
    const originalToken = localStorage.getItem('otok')
    localStorage.setItem(
      getAuth0TokenCacheKey(),
      JSON.stringify(
        update(data, { body: { access_token: { $set: originalToken } } }),
      ),
    )
    localStorage.removeItem('impersonate')
    localStorage.removeItem('otok')
    localStorage.removeItem('selectedClient')
    localStorage.removeItem('selectedFarms')
  }
  try {
    const accessToken = await getAdminAccessToken()
    if (!accessToken) throw new Error('admin access token unavailable')
    await switchClient(tenantId, { redirectTo, accessToken })()
  } catch (error) {
    // The app still holds the visited tenant's token in memory; reload as the admin.
    if (restoredOriginalToken) window.location.href = window.location.pathname
    throw error
  }
}

export const editUserAccount = (data) => {
  const accountData = { ...data }
  delete accountData.email

  return (dispatch) => {
    return http
      .put('/user', accountData)
      .then((res) => {
        dispatch(shouldEditUserAccount(res.data))
      })
      .catch(() => {})
  }
}

// Self-service email change. `initiateEmailChange` requires a recently
// re-authenticated access token (see components/user-details/change-email.jsx
// for the loginWithPopup step-up) — the API rejects a stale one with 403.
// Errors are surfaced to the caller (suppressErrorModal) rather than the
// global alert modal, since the change-email dialog renders its own message
// and must stay open for the user to correct their input.
export const initiateEmailChange = (newEmail) => {
  return () => {
    return http.post(
      '/user/email-change',
      { newEmail },
      { suppressErrorModal: true },
    )
  }
}

export const fetchEmailChangeStatus = () => {
  return () => {
    return http.get('/user/email-change', { suppressErrorModal: true })
  }
}

export const cancelEmailChange = () => {
  return () => {
    return http.delete('/user/email-change', { suppressErrorModal: true })
  }
}

export const changeAccountStatus = (tenantId, status) => {
  return (dispatch) => {
    return http
      .post('partner/change-tenant-status', {
        tenantId,
        accountEnabled: status,
      })
      .then((res) => {
        dispatch(updateClient(res.data))
      })
  }
}

export const getPartnerContactInfo = () => {
  const partner = getPartner()
  return (dispatch) => {
    return http.get(`partner/contact-details/${partner}`).then((res) => {
      dispatch(contactInfoFetched(res.data))
    })
  }
}

export const getUserList = () => {
  return (dispatch) => {
    return http.get(`/user/list`).then((res) => {
      dispatch(userListFetched(res.data))
    })
  }
}

export const editUserPreferences = (data) => {
  return (dispatch) => {
    return http
      .put(`/user/prefs`, data)
      .then((res) => {
        dispatch(userPreferenceFetched(res.data))
      })
      .catch(() => {})
  }
}

export const fetchUserPreferences = () => {
  return (dispatch) => {
    return http
      .get(`/user/prefs`)
      .then((res) => {
        dispatch(userPreferenceFetched(res.data))
      })
      .catch(() => {})
  }
}
